import { LockClosedIcon, LockOpenIcon } from '@heroicons/react/24/outline';
import { useCustomerMode } from '@plato/app-core/hooks/use-customer-mode';
import { Button } from '@plato/ui-legacy/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from '@plato/ui-legacy/components/ui/dropdown-menu';
import { Label } from '@plato/ui-legacy/components/ui/label';
import { Switch } from '@plato/ui-legacy/components/ui/toggle';
import { cn } from '@plato/ui/util/classes';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

export type CustomerModeToggleProps = {
    isExpanded?: boolean;
    className?: string;
    onClose?: () => void;
} & React.InputHTMLAttributes<HTMLInputElement>;

const CustomerModeToggle = React.forwardRef<HTMLInputElement, CustomerModeToggleProps>(
    ({ isExpanded = true }) => {
        const [customerViewToggled, setCustomerViewToggled] = useCustomerMode();
        const { t } = useTranslation();
        const [open, setOpen] = React.useState(false);

        if (isExpanded) {
            return (
                <div className="flex flex-auto justify-between">
                    <div className="flex items-center gap-2 px-3">
                        {customerViewToggled ? (
                            <LockClosedIcon className="h-4 w-4 text-content-weaker" />
                        ) : (
                            <LockOpenIcon className="h-4 w-4 text-content-weaker" />
                        )}
                        <Label
                            htmlFor="limited-view"
                            className={cn(
                                'flex h-8 cursor-pointer items-center gap-2',
                                'text-sm font-normal text-neutral-950',
                            )}
                        >
                            {t('customer_mode__button')}
                        </Label>
                    </div>
                    <Switch
                        id="limited-view"
                        checked={customerViewToggled}
                        onCheckedChange={setCustomerViewToggled}
                    />
                </div>
            );
        }

        return (
            <DropdownMenu open={open} onOpenChange={setOpen}>
                <DropdownMenuTrigger asChild>
                    <Button
                        size="icon-s"
                        variant="ghost"
                        selected={customerViewToggled}
                        onClick={() => {
                            setOpen(true);
                        }}
                        className={cn(
                            'text-sm font-normal',
                            'aria-selected:bg-blue-50 aria-selected:text-blue-700',
                        )}
                    >
                        {customerViewToggled ? (
                            <LockClosedIcon className="flex h-4 w-4 shrink-0 " />
                        ) : (
                            <LockOpenIcon className="flex h-4 w-4 shrink-0 " />
                        )}
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" side="right" className="z-9999">
                    <div className="flex w-[255px] flex-col space-y-2 p-3">
                        <div className="flex items-center space-x-2  text-neutral-600">
                            <Label className="mr-auto text-sm font-medium" htmlFor="limited-view">
                                {t('customer_mode__title')}
                            </Label>
                            <Switch
                                id="limited-view"
                                checked={customerViewToggled}
                                onCheckedChange={setCustomerViewToggled}
                            />
                        </div>
                        <div className="text-sm font-normal text-neutral-600 ">
                            {t('customer_mode__sub_title')}
                        </div>
                    </div>
                </DropdownMenuContent>
            </DropdownMenu>
        );
    },
);
CustomerModeToggle.displayName = 'CustomerModeToggle';

export { CustomerModeToggle };
